/**
 * 개인정보 동의서 타입·순수 함수 (BOOTH-66).
 * 동의서는 운영자가 콘솔에서 발행(ConsentDocument), 키오스크가 손님에게 표시하고 결과를 ConsentRecord 로 기록한다.
 */
// ─── 상수 ─────────────────────────────────────────────────────────────────────
/** 사진·영상 보관 기간(일). 설정 불가 고정값. */
export const GUEST_RETENTION_DAYS = 30;
/** 항목 id — 수집·이용 / 해외 이전 / 디스플레이 표시 */
export const CONSENT_ITEM_IDS = ["collect", "transfer", "display"];
/** 체크 id — 항목 3개 + 연령·동반자 확인 1개 */
export const CONSENT_CHECK_IDS = [...CONSENT_ITEM_IDS, "age"];
/** 전문(body) 한 언어 최대 바이트(글자) 수 */
export const CONSENT_BODY_MAX = 4000;
/** 라벨(label) 한 언어 최대 바이트(글자) 수 */
export const CONSENT_LABEL_MAX = 200;
// ─── 순수 함수 ────────────────────────────────────────────────────────────────
/**
 * 동의 항목 라벨의 현지화 텍스트를 반환한다.
 * 해당 언어 값이 비어 있으면 ko(label) 로 폴백 — i18n.ts localizedName 과 같은 규칙.
 */
export function localizedConsentText(fields, language) {
    const pick = (v) => (v && v.trim() ? v : fields.label);
    switch (language) {
        case "en":
            return pick(fields.labelEn);
        case "ja":
            return pick(fields.labelJa);
        case "zh":
            return pick(fields.labelZh);
        default:
            return fields.label;
    }
}
/**
 * 동의 항목 전문(body)의 현지화 텍스트를 반환한다.
 * 해당 언어 값이 비어 있으면 ko(body) 로 폴백.
 */
export function localizedConsentBody(item, language) {
    const pick = (v) => (v && v.trim() ? v : item.body);
    switch (language) {
        case "en":
            return pick(item.bodyEn);
        case "ja":
            return pick(item.bodyJa);
        case "zh":
            return pick(item.bodyZh);
        default:
            return item.body;
    }
}
/** 텍스트 내 모든 `{days}` 를 days 로 치환한다. */
export function interpolateRetention(text, days) {
    return text.replaceAll("{days}", String(days));
}
/** 모든 체크가 false 인 초기 상태를 생성한다. */
export function emptyChecks() {
    return { collect: false, transfer: false, display: false, age: false };
}
/** 모든 체크가 true 인지 확인한다. */
export function allChecked(checks) {
    return CONSENT_CHECK_IDS.every((id) => checks[id]);
}
/**
 * required 항목과 age 가 모두 true 인지 확인한다.
 * age 는 required 속성 없이 항상 필수로 취급한다.
 */
export function allRequiredChecked(doc, checks) {
    // age 는 별도 항목이라 항상 필수
    if (!checks.age)
        return false;
    // required 항목 검사
    for (const id of CONSENT_ITEM_IDS) {
        if (doc.items[id].required && !checks[id])
            return false;
    }
    return true;
}
/**
 * 전체 토글 — 전부 true 면 전부 false 로, 하나라도 false 면 전부 true 로.
 * 원본 checks 는 변경하지 않고 새 객체를 반환한다.
 */
export function toggleAll(checks) {
    const all = allChecked(checks);
    const value = !all;
    return { collect: value, transfer: value, display: value, age: value };
}
/**
 * 동의서의 모든 텍스트 필드에서 `{days}` 를 `doc.retentionDays` 로 치환한 사본을 반환한다.
 * 원본 doc 은 변경하지 않는다.
 */
export function consentDocWithRetention(doc) {
    const days = doc.retentionDays;
    const replaceItem = (item) => ({
        ...item,
        label: interpolateRetention(item.label, days),
        ...(item.labelEn !== undefined ? { labelEn: interpolateRetention(item.labelEn, days) } : {}),
        ...(item.labelJa !== undefined ? { labelJa: interpolateRetention(item.labelJa, days) } : {}),
        ...(item.labelZh !== undefined ? { labelZh: interpolateRetention(item.labelZh, days) } : {}),
        body: interpolateRetention(item.body, days),
        ...(item.bodyEn !== undefined ? { bodyEn: interpolateRetention(item.bodyEn, days) } : {}),
        ...(item.bodyJa !== undefined ? { bodyJa: interpolateRetention(item.bodyJa, days) } : {}),
        ...(item.bodyZh !== undefined ? { bodyZh: interpolateRetention(item.bodyZh, days) } : {}),
    });
    // 연령·동반자 확인 라벨도 같은 규칙으로 치환 — 서버 검증(`checkPlaceholders`)이 age 라벨의 `{days}` 를
    // 허용하고 콘솔 미리보기도 치환하므로, 여기서 빼면 운영자가 본 화면과 손님 화면이 달라진다.
    const replaceAge = (age) => ({
        ...age,
        label: interpolateRetention(age.label, days),
        ...(age.labelEn !== undefined ? { labelEn: interpolateRetention(age.labelEn, days) } : {}),
        ...(age.labelJa !== undefined ? { labelJa: interpolateRetention(age.labelJa, days) } : {}),
        ...(age.labelZh !== undefined ? { labelZh: interpolateRetention(age.labelZh, days) } : {}),
    });
    return {
        ...doc,
        items: {
            collect: replaceItem(doc.items.collect),
            transfer: replaceItem(doc.items.transfer),
            display: replaceItem(doc.items.display),
        },
        age: replaceAge(doc.age),
    };
}
//# sourceMappingURL=consent.js.map